import { keepPreviousData, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useCallback, useMemo } from 'react';
import { deserializePlaceRef, PlaceRefResponse } from 'src/entities/Course.entity';

const PLACE_SEARCH_PATH = '/api/v5/master/places';
const QUERY_KEY = 'placeSearch';
/** 1 回に頼む件数。候補の一番下までスクロールしたら次の 50 件を継ぎ足す（log_web_issues#384） */
export const PLACE_PAGE_SIZE = 50;

/**
 * 地点コードだけ・名前だけ・住所だけで引く（「納品先を追加」の納品先コード欄・納品先名欄 #379、住所の欄 #384）。
 * 指定しなければ名前・地点コードの両方
 */
export type PlaceSearchBy = 'internal_location_code' | 'name' | 'address';

/**
 * 地点マスタを引く（#362）。会社は API がログイン会社（切替中は選択中）で絞る。
 * 空の語でも enabled なら全地点を 1 ページ目から引き、fetchNextPage で offset を進めて継ぎ足す（#384）
 */
export const usePlaceSearchQuery = (q: string, by: PlaceSearchBy | undefined, enabled: boolean) => {
  const trimmed = q.trim();
  const queryClient = useQueryClient();
  /**
   * 閉じた欄（enabled でない）の地点検索のキャッシュを捨てる。欄を閉じた後に呼び、開き直しは 1 ページ目から取る。
   * Why not 残す: staleTime 0 なので、開き直すと継ぎ足した全ページを先頭から取り直す。
   * Why not 全部消す: 「納品先を追加」で別の欄へ直接移ると、開いた欄の取得中の検索まで消えて読み込み中のまま止まる
   */
  const reset = useCallback(() => queryClient.removeQueries({ queryKey: [QUERY_KEY], type: 'inactive' }), [queryClient]);
  const query = useInfiniteQuery({
    // by も key に入れる: 同じ語でも納品先コード欄と納品先名欄で結果が違う
    queryKey: [QUERY_KEY, trimmed, by ?? null],
    queryFn: async ({ pageParam }) => (await axios.get<PlaceRefResponse[]>(PLACE_SEARCH_PATH, {
      params: { q: trimmed, limit: PLACE_PAGE_SIZE, offset: pageParam, ...(by && { by }) },
    })).data.map(deserializePlaceRef),
    initialPageParam: 0,
    getNextPageParam: (last, all) => (last.length < PLACE_PAGE_SIZE ? undefined : all.length * PLACE_PAGE_SIZE),
    enabled,
    // 入力中に候補が一瞬消えないよう、前の結果を次の結果が来るまで見せる
    placeholderData: keepPreviousData,
    // Why not 既定（focus で取り直す）: 継ぎ足した全ページを先頭から取り直す（1 社最大 2.8 万地点＝573 ページ）
    refetchOnWindowFocus: false,
  });
  // 前の語の結果を見せている間は 1 ページ目だけにし、継ぎ足さない（前の語の 2 ページ目以降を新しい語の候補にしない）
  const pages = query.data?.pages;
  const shownPages = query.isPlaceholderData ? pages?.slice(0, 1) : pages;
  const places = useMemo(() => {
    const seen = new Set<number>();
    // 継ぎ足しの間に地点が増えると offset がずれて同じ地点が次のページにも来るので、id で 1 回だけにする
    return (shownPages ?? []).flat().filter((p) => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });
  }, [shownPages]);
  return {
    places,
    isFetching: query.isFetching,
    hasNextPage: !query.isPlaceholderData && query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: () => { void query.fetchNextPage(); },
    reset,
  };
};
